"use client";

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { downloadFile, downloadFiles } from "../../lib/download";
import dynamic from "next/dynamic";
import { patchTaskOutputImage, startStitch, getStitchStatus } from "../../lib/ecom-image-api";
import { imageEditApi } from "../../lib/image-edit-api";
import type { EcomActiveRefineState, EcomRefineTrackInfo } from "../../lib/ecom-tab-history";
import RefineOverlay from "./RefineOverlay";
import { useRefineLifecycle } from "./useRefineLifecycle";
import { resolveMediaUrl } from "../../lib/resolve-media-url";
import { buildLayoutDetailOrder, getLayoutSlotState } from "../../lib/ecom-layout-output";
import ImageEditModal, { type ImageEditSaveResult } from "../image-edit/ImageEditModal";
import { ActionIconButton } from "./ImageActionToolbar";
import ImageEditActionIcon from "./ImageEditActionIcon";
import { replaceEditedImages } from "../../lib/image-edit-save";
import { useLatestRefs } from "../../hooks/useLatestRefs";
import {
  createToneAdjustedBlob,
  loadToneRecords,
  saveToneRecords,
  type ToneRecord,
} from "../../lib/image-tone";

const ImageRefineEditor = dynamic(
  () => import("./ImageRefineEditor"),
  { ssr: false }
);

function DownloadIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 1024 1024" aria-hidden="true">
      <path
        d="M523.3664 993.8944c-5.3248 2.56-7.7824 2.56-13.1072 2.56-5.2736 0-10.3936 0-13.056-2.56-5.3248-2.6112-7.7824-5.3248-10.3936-7.7824L43.1104 568.4736a40.192 40.192 0 0 1-2.6112-54.784 40.192 40.192 0 0 1 54.7328-2.56l378.4704 354.816V70.144c0-20.8896 18.2784-39.168 39.168-39.168 20.8896 0 39.1168 18.2784 39.1168 39.168v795.8016l375.9104-354.816a37.6832 37.6832 0 0 1 54.7328 2.56 37.4272 37.4272 0 0 1 10.3936 26.112 42.0864 42.0864 0 0 1-13.1072 28.672l-443.5968 417.536c-2.56 2.6624-7.7824 5.12-12.9536 7.8848z"
        fill="currentColor"
      />
    </svg>
  );
}

function UploadIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3.82064 12.6539C2.8725 12.6539 1.96 12.2925 1.26894 11.6433C0.577884 10.9942 0.160234 10.106 0.101028 9.15973C0.0418227 8.21344 0.345514 7.28016 0.950266 6.54991C1.55502 5.81967 2.41536 5.34738 3.35611 5.2292V5.16498C3.35623 4.07401 3.75741 3.02115 4.48332 2.20674C5.20923 1.39232 6.2092 0.873176 7.29297 0.748087C8.37675 0.622998 9.46869 0.900691 10.361 1.52833C11.2534 2.15597 11.8839 3.08976 12.1325 4.15201C13.2082 4.28755 14.1917 4.82793 14.883 5.66314C15.5742 6.49835 15.9212 7.5656 15.8532 8.64762C15.7852 9.72964 15.3075 10.7451 14.5171 11.4872C13.7268 12.2294 12.6834 12.6424 11.5992 12.6423C11.4774 12.6401 11.3612 12.5901 11.2758 12.5031C11.1904 12.4162 11.1426 12.2991 11.1426 12.1772C11.1426 12.0554 11.1904 11.9383 11.2758 11.8514C11.3612 11.7644 11.4774 11.7144 11.5992 11.7122C12.472 11.7122 13.3099 11.3697 13.9328 10.7584C14.5557 10.1471 14.9139 9.31573 14.9303 8.44312C14.9466 7.5705 14.6199 6.72631 14.0204 6.09205C13.4209 5.45779 12.5964 5.08412 11.7242 5.05139L11.337 5.03701L11.2808 4.65358C11.1514 3.77491 10.6944 2.97774 10.0015 2.42217C9.30858 1.86661 8.43111 1.59381 7.54534 1.65856C6.65957 1.72331 5.83111 2.12083 5.22636 2.77125C4.62161 3.42168 4.28537 4.27685 4.28517 5.16498C4.28523 5.31312 4.29446 5.46111 4.31283 5.60811L4.37923 6.13608L3.83095 6.13092H3.82017C2.27814 6.13092 1.0233 7.38545 1.0233 8.9278C1.0233 10.4701 2.27783 11.7247 3.82017 11.7247C3.94204 11.7269 4.05817 11.7769 4.14356 11.8639C4.22896 11.9508 4.2768 12.0679 4.2768 12.1897C4.2768 12.3116 4.22896 12.4287 4.14356 12.5156C4.05817 12.6026 3.94204 12.6526 3.82017 12.6548L3.82064 12.6539Z" fill="currentColor" />
      <path d="M7.63281 6.65381H8.32031C8.42448 6.65381 8.47656 6.70589 8.47656 6.81006V13.9893C8.47656 14.0934 8.42448 14.1455 8.32031 14.1455H7.63281C7.52865 14.1455 7.47656 14.0934 7.47656 13.9893V6.81006C7.47656 6.70589 7.52865 6.65381 7.63281 6.65381Z" fill="currentColor" />
      <path d="M7.39509 6.54138L7.88122 6.05524C7.95488 5.98159 8.02854 5.98159 8.10219 6.05524L10.6395 8.59254C10.7131 8.6662 10.7131 8.73985 10.6395 8.81351L10.1534 9.29965C10.0797 9.37331 10.006 9.37331 9.93238 9.29965L7.39509 6.76235C7.32143 6.68869 7.32143 6.61504 7.39509 6.54138Z" fill="currentColor" />
      <path d="M5.29352 8.65474L7.89303 6.05524C7.96668 5.98159 8.04034 5.98159 8.114 6.05524L8.60013 6.54138C8.67379 6.61504 8.67379 6.68869 8.60013 6.76235L6.00063 9.36185C5.92697 9.43551 5.85332 9.43551 5.77966 9.36185L5.29352 8.87571C5.21987 8.80206 5.21987 8.7284 5.29352 8.65474Z" fill="currentColor" />
    </svg>
  );
}

type ApiEnvelope<T> = {
  data?: T;
};

type StitchTaskData = {
  task_id?: string;
  status?: string;
  result_url?: string;
  error?: string;
};

function getResponseData<T>(response: unknown): T | undefined {
  return (response as ApiEnvelope<T> | undefined)?.data;
}

interface ImageStitchPanelProps {
  images: string[];
  status: "empty" | "generating" | "completed" | "failed";
  progress: number;
  taskProgresses: number[];
  outputCount: number;
  onPreview?: (url: string, index?: number) => void;
  onPublish?: (imageUrl: string) => void;
  onFavorite?: (imageUrl: string) => void;
  onRegenerateSingle?: (index: number) => void;
  regeneratingIndexes?: number[];
  failedIndexes?: number[];
  statusHint?: string;
  taskId?: string;
  batchChildTaskIds?: string[];
  productImage?: string;
  onImagesUpdated?: (images: string[], targetTaskId?: string) => void;
  onHistoryRefresh?: () => void;
  onExitStitch?: () => void;
  refineOwnerTabId: string;
  onRefineTrack?: (info: EcomRefineTrackInfo) => boolean | void;
  activeRefines?: Record<number, EcomActiveRefineState>;
  refineStateVersion?: number;
}


function ProgressRing({ size = 60 }: { progress?: number; size?: number }) {
  const r = size * 0.42;
  const strokeW = size >= 80 ? 5 : 3;
  const circumference = 2 * Math.PI * r;
  const center = size / 2;

  return (
    <div className="relative z-10" style={{ animation: "ecomRingPulse 2.5s ease-in-out infinite" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={center} cy={center} r={r} stroke="rgba(255,255,255,0.15)" fill="none" strokeWidth={strokeW} />
        <circle
          cx={center} cy={center} r={r}
          stroke="rgba(255,255,255,0.9)" fill="none" strokeWidth={strokeW}
          strokeDasharray={`${circumference * 0.25} ${circumference * 0.75}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
          style={{ transformOrigin: "center", animation: "ecomSpinRing 1.2s linear infinite" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-bold text-white text-[8px] leading-tight" style={{ animation: "ecomPulseText 1.5s ease-in-out infinite" }}>AI</span>
        <span className="font-medium text-white/80 text-[6px] leading-tight">Generating</span>
      </div>
    </div>
  );
}

function GeneratingPlaceholder({ progress, statusHint }: { progress: number; statusHint?: string }) {
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden"
      style={{
        borderRadius: "9.23px",
        background: "linear-gradient(135deg, #6BBDFC 0%, #9886FB 25%, #C4B1ED 50%, #E2A7CE 75%, #C4B1ED 100%)",
        backgroundSize: "300% 300%",
        animation: "ecomGradientShift 4s ease infinite",
      }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.25) 50%, transparent 60%)",
            backgroundSize: "200% 100%",
            animation: "ecomShimmer 2.5s ease-in-out infinite",
          }}
        />
      </div>
      <ProgressRing progress={progress} size={48} />
      <span className="text-white/80 mt-2 relative z-10 text-[10px]">
        {statusHint || "生成中..."}
      </span>
    </div>
  );
}

export default function ImageStitchPanel({
  images: rawImages,
  status,
  taskProgresses,
  outputCount,
  onPreview,
  onPublish,
  onFavorite,
  onRegenerateSingle,
  regeneratingIndexes = [],
  failedIndexes = [],
  statusHint,
  taskId = "",
  batchChildTaskIds = [],
  productImage: rawProductImage,
  onImagesUpdated,
  onHistoryRefresh,
  onExitStitch,
  refineOwnerTabId,
  onRefineTrack,
  activeRefines,
  refineStateVersion,
}: ImageStitchPanelProps) {
  const images = useMemo(
    () => rawImages.map((img) => resolveMediaUrl(img)),
    [rawImages]
  );
  const productImage = useMemo(() => resolveMediaUrl(rawProductImage), [rawProductImage]);
  const [displayOrder, setDisplayOrder] = useState<number[]>([]);
  const [isReordering, setIsReordering] = useState(false);
  const [reorderSequence, setReorderSequence] = useState<number[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [stitchedPreviewUrl, setStitchedPreviewUrl] = useState<string | null>(null);
  const stitchedBlobUrlRef = useRef<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [stitchTaskId, setStitchTaskId] = useState<string | null>(null);
  const stitchPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [zoomLevel, setZoomLevel] = useState(30);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const wheelListenerRef = useRef<((e: WheelEvent) => void) | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ y: 0, scrollTop: 0 });

  // Refine editor state
  const [refineEditorOpen, setRefineEditorOpen] = useState(false);
  const [refineImageUrl, setRefineImageUrl] = useState("");
  const [refineImageIndex, setRefineImageIndex] = useState(0);
  const [imageEditOpen, setImageEditOpen] = useState(false);
  const [imageEditInitialId, setImageEditInitialId] = useState("");
  const [toastMessage, setToastMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // Batch download state
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  const {
    images: imagesRef,
    onImagesUpdated: onImagesUpdatedRef,
    taskId: taskIdRef,
    batchChildTaskIds: batchChildTaskIdsRef,
    onHistoryRefresh: onHistoryRefreshRef,
  } = useLatestRefs({ images, onImagesUpdated, taskId, batchChildTaskIds, onHistoryRefresh });
  const { refiningIndexes, handleRefineStart } = useRefineLifecycle({
    ownerTabId: refineOwnerTabId,
    imageIndex: refineImageIndex,
    taskId,
    batchChildTaskIds,
    activeRefines,
    sourceImages: images,
    lifecycleVersion: refineStateVersion,
    onTrack: onRefineTrack,
    onHistoryRefresh,
    onFailure: (message) => setToastMessage({ text: message, type: "error" }),
  });

  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(null), 3000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  const handleOpenRefine = useCallback((imgUrl: string, imgIndex: number) => {
    setRefineImageUrl(imgUrl);
    setRefineImageIndex(imgIndex);
    setRefineEditorOpen(true);
  }, []);

  const imageEditItems = useMemo(
    () => images
      .map((url, idx) => ({ id: `stitch-image-${idx}`, url, index: idx }))
      .filter((item) => !!item.url),
    [images]
  );

  const handleOpenImageEdit = useCallback((idx: number) => {
    const item = imageEditItems.find((candidate) => candidate.index === idx) || imageEditItems[0];
    if (!item) {
      setToastMessage({ text: "E8101 编辑参数有误，请调整后重试", type: "error" });
      return;
    }
    setImageEditInitialId(item.id);
    setImageEditOpen(true);
  }, [imageEditItems]);

  const handleImageEditSave = useCallback(async (
    results: ImageEditSaveResult[],
    options?: { silent?: boolean },
  ) => {
    if (results.length === 0) return;
    const resolved = replaceEditedImages(imagesRef.current, results);
    if (resolved.replacements.length === 0) {
      throw new Error("E8102 图片保存失败，请稍后重试");
    }

    const patchJobs = resolved.replacements.map((replacement) => {
      const targetTaskId = batchChildTaskIdsRef.current?.[replacement.targetIndex] || taskIdRef.current;
      if (!targetTaskId) throw new Error("E8102 图片保存失败，请稍后重试");
      const patchIndex = batchChildTaskIdsRef.current?.[replacement.targetIndex]
        ? 0
        : replacement.targetIndex;
      return patchTaskOutputImage(targetTaskId, patchIndex, replacement.editedUrl);
    });

    await Promise.all(patchJobs);
    onImagesUpdatedRef.current?.(resolved.images, taskIdRef.current);
    onHistoryRefreshRef.current?.();
    if (!options?.silent) {
      setToastMessage({ text: "调色结果已保存", type: "success" });
    }
  }, []);

  // 快捷调色与主预览区共用同一套基准图、像素管线和持久化记录。
  // 右侧详情预览和左侧缩略图都读取 tonePreview，因此滑杆预览不会只更新一侧。
  const [tonePreview, setTonePreview] = useState<{ idx: number; url: string } | null>(null);
  const tonePreviewClearRef = useRef<{ idx: number; previewUrl: string; sourceUrl: string } | null>(null);
  const tonePreviewSourceRef = useRef(images);
  const tonePreviewBaseSourceRef = useRef<Record<number, string>>({});
  const tonePreviewRef = useRef<{ idx: number; url: string } | null>(null);
  const tonePreviewSwapTimeoutsRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const [toneApplyingIndexes, setToneApplyingIndexes] = useState<Set<number>>(new Set());
  const [toneRecords, setToneRecords] = useState<Record<string, ToneRecord>>({});
  const toneRecordsRef = useRef<Record<string, ToneRecord>>({});

  tonePreviewRef.current = tonePreview;
  toneRecordsRef.current = toneRecords;

  useEffect(() => {
    setToneRecords(loadToneRecords());
  }, [taskId]);

  const toneRecordKey = useCallback(
    (idx: number) => `${taskIdRef.current || "local"}:${idx}`,
    [],
  );

  const updateToneRecords = useCallback((
    updates: Array<{ key: string; record: ToneRecord | null }>,
  ) => {
    setToneRecords((prev) => {
      const next = { ...prev };
      for (const update of updates) {
        if (update.record) next[update.key] = update.record;
        else delete next[update.key];
      }
      saveToneRecords(next);
      toneRecordsRef.current = next;
      return next;
    });
  }, []);

  const finalizeTonePreviewSwap = useCallback((pending: { idx: number; previewUrl: string; sourceUrl: string }) => {
    if (tonePreviewClearRef.current !== pending) return;
    const timeout = tonePreviewSwapTimeoutsRef.current[pending.idx];
    if (timeout) {
      clearTimeout(timeout);
      delete tonePreviewSwapTimeoutsRef.current[pending.idx];
    }
    tonePreviewClearRef.current = null;
    tonePreviewRef.current = null;
    setTonePreview((current) => (
      current?.idx === pending.idx && current.url === pending.previewUrl ? null : current
    ));
    setToneApplyingIndexes((prev) => {
      const next = new Set(prev);
      next.delete(pending.idx);
      return next;
    });
  }, []);

  const tryDecodeTonePreviewSwap = useCallback((pending: { idx: number; previewUrl: string; sourceUrl: string }) => {
    const nextUrl = tonePreviewSourceRef.current[pending.idx];
    if (!nextUrl || nextUrl === pending.sourceUrl) return;

    const image = new Image();
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      finalizeTonePreviewSwap(pending);
    };
    image.onload = settle;
    image.onerror = settle;
    image.src = nextUrl;
  }, [finalizeTonePreviewSwap]);

  useEffect(() => {
    tonePreviewSourceRef.current = images;
    const pending = tonePreviewClearRef.current;
    if (pending) tryDecodeTonePreviewSwap(pending);
  }, [images, tryDecodeTonePreviewSwap]);

  const handleTonePreviewChange = useCallback((idx: number, url: string | null) => {
    if (url) {
      const previousPending = tonePreviewClearRef.current;
      if (previousPending) finalizeTonePreviewSwap(previousPending);
      tonePreviewBaseSourceRef.current[idx] = tonePreviewSourceRef.current[idx] || "";
      tonePreviewRef.current = { idx, url };
      setTonePreview({ idx, url });
      return;
    }
    const current = tonePreviewRef.current;
    if (!current || current.idx !== idx) return;
    const pending = {
      idx,
      previewUrl: current.url,
      sourceUrl: tonePreviewBaseSourceRef.current[idx] || "",
    };
    tonePreviewClearRef.current = pending;
    tonePreviewSwapTimeoutsRef.current[idx] = setTimeout(() => {
      finalizeTonePreviewSwap(pending);
    }, 5000);
    tryDecodeTonePreviewSwap(pending);
  }, [finalizeTonePreviewSwap, tryDecodeTonePreviewSwap]);

  useEffect(() => () => {
    Object.values(tonePreviewSwapTimeoutsRef.current).forEach(clearTimeout);
    tonePreviewSwapTimeoutsRef.current = {};
  }, []);

  const handleApplyTone = useCallback(async (
    idx: number,
    tone: number,
    scope: "single" | "batch",
  ) => {
    const targets = scope === "single"
      ? imageEditItems.filter((item) => item.index === idx)
      : imageEditItems;
    if (targets.length === 0) {
      setToastMessage({ text: "E8101 编辑参数有误，请调整后重试", type: "error" });
      throw new Error("E8101");
    }
    const targetIndexes = targets.map((item) => item.index);
    setToneApplyingIndexes((prev) => {
      const next = new Set(prev);
      targetIndexes.forEach((targetIndex) => next.add(targetIndex));
      return next;
    });
    let waitingForPreviewSwap = false;
    try {
      const jobs = await Promise.all(targets.map(async (item) => {
        const key = toneRecordKey(item.index);
        const record = toneRecordsRef.current[key];
        const baseUrl = record?.sourceUrl || item.url;
        if (tone === 0) {
          if (!record) return null;
          return {
            key,
            record: null as ToneRecord | null,
            result: { sourceIndex: item.index, sourceUrl: item.url, editedUrl: baseUrl },
          };
        }
        const blob = await createToneAdjustedBlob(baseUrl, tone);
        const uploaded = await imageEditApi.uploadEditedImage(blob, taskIdRef.current || "", item.index);
        return {
          key,
          record: { tone, sourceUrl: baseUrl, updatedAt: Date.now() } as ToneRecord,
          result: { sourceIndex: item.index, sourceUrl: item.url, editedUrl: uploaded.url },
        };
      }));
      const validJobs = jobs.filter((job): job is NonNullable<typeof job> => job !== null);
      if (validJobs.length === 0) return;
      await handleImageEditSave(validJobs.map((job) => job.result), { silent: true });
      updateToneRecords(validJobs.map((job) => ({ key: job.key, record: job.record })));
      waitingForPreviewSwap = scope === "single" && tonePreviewRef.current?.idx === idx;
    } catch (err) {
      setToastMessage({ text: "E8102 图片保存失败，请稍后重试", type: "error" });
      throw err;
    } finally {
      if (!waitingForPreviewSwap) {
        setToneApplyingIndexes((prev) => {
          const next = new Set(prev);
          targetIndexes.forEach((targetIndex) => next.delete(targetIndex));
          return next;
        });
      }
    }
  }, [handleImageEditSave, imageEditItems, toneRecordKey, updateToneRecords]);

  // Quick tone controls were removed from the toolbar; retain the persistence
  // pipeline for backwards-compatible saved records without exposing it here.
  void handleTonePreviewChange;
  void handleApplyTone;

  const imageActionButtonClass =
    "w-6 h-6 rounded bg-black/70 hover:bg-black/90 flex items-center justify-center text-white/65 hover:text-white backdrop-blur-xs transition-colors";
  const imageActionColumnClass =
    "absolute right-1.5 top-1.5 z-20 flex flex-col gap-1 opacity-0 transition-opacity group-hover/card:opacity-100 group-focus-within/card:opacity-100 group-hover/stitch-item:opacity-100 group-focus-within/stitch-item:opacity-100";

  const renderImageInlineActions = (imgUrl: string, idx: number) => (
    <div
      className={imageActionColumnClass}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <ActionIconButton
        label="下载"
        className={imageActionButtonClass}
        onClick={(e) => handleSingleDownload(e, imgUrl, idx)}
      >
        <DownloadIcon className="h-3.5 w-3.5" />
      </ActionIconButton>
      <ActionIconButton
        label="AI重绘"
        className={`${imageActionButtonClass} text-[13px] font-semibold leading-none`}
        onClick={(e) => { e.stopPropagation(); handleOpenRefine(imgUrl, idx); }}
      >
        Ai
      </ActionIconButton>
      <ActionIconButton
        label="图像编辑"
        className={imageActionButtonClass}
        onClick={(e) => { e.stopPropagation(); handleOpenImageEdit(idx); }}
      >
        <ImageEditActionIcon className="h-3.5 w-3.5" />
      </ActionIconButton>
      {onPublish && (
        <ActionIconButton
          label="发布灵感社区"
          className={imageActionButtonClass}
          onClick={(e) => { e.stopPropagation(); onPublish(imgUrl); }}
        >
          <UploadIcon className="h-3.5 w-3.5" />
        </ActionIconButton>
      )}
      {onFavorite && (
        <ActionIconButton
          label="收藏"
          className={imageActionButtonClass}
          onClick={(e) => { e.stopPropagation(); onFavorite(imgUrl); }}
        >
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </ActionIconButton>
      )}
      {onRegenerateSingle && (
        <ActionIconButton
          label="重新生成"
          className={imageActionButtonClass}
          onClick={(e) => { e.stopPropagation(); onRegenerateSingle(idx); }}
        >
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </ActionIconButton>
      )}
    </div>
  );

  const renderRefineOverlay = (idx: number) => {
    if ((activeRefines && idx in activeRefines) || idx in refiningIndexes) {
      return <RefineOverlay visible={true} startedAt={activeRefines?.[idx]?.startedAt} />;
    }
    return null;
  };

  useEffect(() => {
    setDisplayOrder(Array.from({ length: outputCount }, (_, i) => i));
  }, [outputCount]);

  const validImages = images.filter(Boolean);
  const allCompleted = status === "completed";

  const currentViewOrder =
    isReordering
      ? reorderSequence
      : displayOrder;

  // 一键排版"详情预览"必须剔除失败 / 完成后仍无图的 slot，避免它们以"生成中"
  // 占位卡的形式出现在右侧详情列表里。仅生成进行中且未标记失败的无图 slot
  // 才会保留为真实的生成中占位。
  const layoutDetailOrder = useMemo(
    () =>
      buildLayoutDetailOrder({
        baseOrder: currentViewOrder,
        images,
        failedIndexes,
        regeneratingIndexes,
        status,
      }),
    [currentViewOrder, images, failedIndexes, regeneratingIndexes, status]
  );

  const startReorder = () => {
    setIsReordering(true);
    setReorderSequence([]);
  };

  const handleReorderClick = (idx: number) => {
    if (!isReordering || !images[idx]) return;
    setReorderSequence((prev) => {
      if (prev.includes(idx)) {
        return prev.filter((i) => i !== idx);
      }
      return [...prev, idx];
    });
  };

  const confirmReorder = () => {
    if (reorderSequence.length > 0) {
      setDisplayOrder([...reorderSequence]);
    }
    setIsReordering(false);
    setReorderSequence([]);
  };

  const revokeStitchedUrl = useCallback(() => {
    if (stitchedBlobUrlRef.current) {
      URL.revokeObjectURL(stitchedBlobUrlRef.current);
      stitchedBlobUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      revokeStitchedUrl();
      if (stitchPollRef.current) clearInterval(stitchPollRef.current);
    };
  }, [revokeStitchedUrl]);

  // Poll backend stitch task until completed/failed
  const pollStitchResult = useCallback((taskId: string, onDone: (url: string) => void) => {
    if (stitchPollRef.current) clearInterval(stitchPollRef.current);
    let count = 0;
    const maxPolls = 120;
    stitchPollRef.current = setInterval(async () => {
      count++;
      if (count > maxPolls) {
        if (stitchPollRef.current) clearInterval(stitchPollRef.current);
        stitchPollRef.current = null;
        setIsExporting(false);
        setStitchTaskId(null);
        setToastMessage({ text: "拼接超时，请重试", type: "error" });
        return;
      }
      try {
        const res = await getStitchStatus(taskId);
        const data = getResponseData<StitchTaskData>(res);
        if (!data) return;
        if (data.status === "completed" && data.result_url) {
          if (stitchPollRef.current) clearInterval(stitchPollRef.current);
          stitchPollRef.current = null;
          setIsExporting(false);
          setStitchTaskId(null);
          onDone(data.result_url);
        } else if (data.status === "failed") {
          if (stitchPollRef.current) clearInterval(stitchPollRef.current);
          stitchPollRef.current = null;
          setIsExporting(false);
          setStitchTaskId(null);
          setToastMessage({ text: data.error || "拼接失败，请重试", type: "error" });
        }
      } catch (err) {
        console.error("Poll stitch error:", err);
      }
    }, 1000);
  }, []);

  // Submit stitch to backend (async, returns immediately)
  const submitStitch = useCallback(async (orderOverride?: number[]): Promise<void> => {
    const order = orderOverride || displayOrder;
    const ordered = order.map((idx) => images[idx]).filter(Boolean);
    if (ordered.length < 2) return;

    setIsExporting(true);
    try {
      const res = await startStitch(ordered, undefined, 92);
      const data = getResponseData<StitchTaskData>(res);
      if (!data?.task_id) {
        setIsExporting(false);
        setToastMessage({ text: "提交拼接任务失败", type: "error" });
        return;
      }
      setStitchTaskId(data.task_id);
      pollStitchResult(data.task_id, (resultUrl) => {
        revokeStitchedUrl();
        setStitchedPreviewUrl(resultUrl);
        setConfirmed(true);
      });
    } catch (err) {
      console.error("Start stitch failed:", err);
      setIsExporting(false);
      setToastMessage({ text: "提交拼接任务失败", type: "error" });
    }
  }, [displayOrder, images, revokeStitchedUrl, pollStitchResult]);

  const confirmStitch = async () => {
    let finalOrder = displayOrder;
    if (isReordering && reorderSequence.length > 0) {
      finalOrder = [...reorderSequence];
      setDisplayOrder(finalOrder);
    }
    setIsReordering(false);
    setReorderSequence([]);
    await submitStitch(finalOrder);
  };

  const handleDownloadStitched = async () => {
    const url = stitchedPreviewUrl;
    if (!url) return;
    await downloadFile(url, `flowx_detail_page_${Date.now()}.jpg`);
    setConfirmed(false);
    revokeStitchedUrl();
    setStitchedPreviewUrl(null);
  };

  const handleResetStitch = () => {
    setConfirmed(false);
    revokeStitchedUrl();
    setStitchedPreviewUrl(null);
  };

  const handleSingleDownload = async (e: React.MouseEvent, url: string, index: number) => {
    e.stopPropagation();
    await downloadFile(url, `flowx_${index + 1}_${Date.now()}.png`);
  };

  // Batch download functions
  const toggleSelectIndex = (idx: number) => {
    setSelectedIndexes(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const handleDownloadAll = async () => {
    setShowDownloadMenu(false);
    const validImages = images.filter(Boolean);
    if (validImages.length === 0) return;
    try {
      await downloadFiles(validImages, "flowx");
      setToastMessage({
        text: validImages.length > 1
          ? `已打包下载 ${validImages.length} 张图片`
          : "下载成功",
        type: "success",
      });
    } catch {
      setToastMessage({ text: "批量下载失败，请稍后重试", type: "error" });
    }
  };

  const handleEnterSelectMode = () => {
    setShowDownloadMenu(false);
    setIsSelectMode(true);
    // 默认全选所有有效图片
    const allValidIndexes = images.map((img, idx) => img ? idx : -1).filter(idx => idx >= 0);
    setSelectedIndexes(new Set(allValidIndexes));
  };

  const handleConfirmSelectedDownload = async () => {
    const selectedUrls = Array.from(selectedIndexes)
      .sort((a, b) => a - b)
      .map(idx => images[idx])
      .filter(Boolean);

    if (selectedUrls.length === 0) {
      setToastMessage({ text: "请至少选择一张图片", type: "error" });
      return;
    }

    try {
      await downloadFiles(selectedUrls, "flowx");
      setToastMessage({
        text: selectedUrls.length > 1
          ? `已打包下载 ${selectedUrls.length} 张图片`
          : "下载成功",
        type: "success",
      });
    } catch {
      setToastMessage({ text: "下载失败，请稍后重试", type: "error" });
      return;
    }
    setIsSelectMode(false);
    setSelectedIndexes(new Set());
  };

  const handleCancelSelectMode = () => {
    setIsSelectMode(false);
    setSelectedIndexes(new Set());
  };

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    if (showDownloadMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDownloadMenu]);

  const handleAutoStitchDownload = async () => {
    // Submit to backend, poll, then download when done
    const order = displayOrder;
    const ordered = order.map((idx) => images[idx]).filter(Boolean);
    if (ordered.length < 2) return;

    setIsExporting(true);
    try {
      const res = await startStitch(ordered, undefined, 92);
      const data = getResponseData<StitchTaskData>(res);
      if (!data?.task_id) {
        setIsExporting(false);
        setToastMessage({ text: "提交拼接任务失败", type: "error" });
        return;
      }
      setStitchTaskId(data.task_id);
      pollStitchResult(data.task_id, async (resultUrl) => {
        await downloadFile(resultUrl, `flowx_detail_page_${Date.now()}.jpg`);
        setStitchedPreviewUrl(null);
      });
    } catch (err) {
      console.error("Auto stitch download failed:", err);
      setIsExporting(false);
      setToastMessage({ text: "提交拼接任务失败", type: "error" });
    }
  };

  if (!wheelListenerRef.current) {
    let rafPending = false;
    wheelListenerRef.current = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (rafPending) return;
      rafPending = true;
      const deltaY = e.deltaY;
      requestAnimationFrame(() => {
        rafPending = false;
        setZoomLevel((prev) => {
          const delta = deltaY > 0 ? -5 : 5;
          return Math.min(200, Math.max(10, prev + delta));
        });
      });
    };
  }

  const scrollContainerCallbackRef = useCallback((node: HTMLDivElement | null) => {
    const prev = scrollContainerRef.current;
    if (prev && wheelListenerRef.current) {
      prev.removeEventListener("wheel", wheelListenerRef.current);
    }
    scrollContainerRef.current = node;
    if (node && wheelListenerRef.current) {
      node.addEventListener("wheel", wheelListenerRef.current, { passive: false });
    }
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    setIsDragging(true);
    dragStartRef.current = { y: e.clientY, scrollTop: container.scrollTop };
    e.preventDefault();
  }, []);

  const handleDragMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const dy = e.clientY - dragStartRef.current.y;
    container.scrollTop = dragStartRef.current.scrollTop - dy;
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const refineEditorModal = refineEditorOpen ? (
    <ImageRefineEditor
      imageUrl={refineImageUrl}
      taskId={taskId}
      imageIndex={refineImageIndex}
      productImage={productImage}
      onClose={() => setRefineEditorOpen(false)}
      onRefineStart={handleRefineStart}
    />
  ) : null;

  const imageEditModal = imageEditOpen ? (
    <ImageEditModal
      open={imageEditOpen}
      mode="single"
      images={imageEditItems}
      initialImageId={imageEditInitialId}
      taskId={taskId}
      onClose={() => setImageEditOpen(false)}
      onSave={async (results) => {
        await handleImageEditSave(results);
        updateToneRecords(results.map((result) => ({
          key: toneRecordKey(result.sourceIndex),
          record: null,
        })));
      }}
    />
  ) : null;

  const toastEl = toastMessage ? (
    <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-200 px-5 py-2.5 rounded-lg text-sm font-medium shadow-lg backdrop-blur-xs animate-[fadeInDown_0.3s_ease-out] ${toastMessage.type === "success"
        ? "bg-green-500/90 text-white"
        : "bg-red-500/90 text-white"
      }`}>
      {toastMessage.text}
    </div>
  ) : null;

  return (
    <div className="h-full flex flex-col">
      {refineEditorModal}
      {imageEditModal}
      {toastEl}
      {/* Top toolbar */}
      <div className="shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-white/10 bg-[#0f0f0f]/80 backdrop-blur-xs">
        <div className="flex items-center gap-2">
          {onExitStitch && (
            <button
              onClick={onExitStitch}
              className="h-8 px-3 flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-lg text-xs text-white/70 hover:text-white transition-colors border border-white/10"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              返回
            </button>
          )}
          <div className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-[#091B24]">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M8.75039 4.89999C8.54039 4.89999 8.40039 5.03999 8.40039 5.24999C8.40039 5.63499 8.29539 5.98499 8.08539 6.22999C7.91039 6.47499 7.56039 6.64999 7.00039 6.64999C6.30039 6.64999 5.74039 6.89499 5.39039 7.31499C5.04039 7.73499 4.90039 8.25999 4.90039 8.74999C4.90039 8.95999 5.04039 9.09999 5.25039 9.09999C5.46039 9.09999 5.60039 8.95999 5.60039 8.74999C5.60039 8.36499 5.70539 8.01499 5.91539 7.76999C6.12539 7.52499 6.47539 7.34999 7.00039 7.34999C7.73539 7.34999 8.29539 7.10499 8.64539 6.68499C8.99539 6.26499 9.10039 5.73999 9.10039 5.24999C9.10039 5.03999 8.96039 4.89999 8.75039 4.89999Z" fill="#27ADBD" />
              <path d="M1.05078 2.97501H12.9508V3.67501H1.05078V2.97501ZM1.05078 10.325H12.9508V11.025H1.05078V10.325Z" fill="#27ADBD" />
              <path d="M2.97461 1.05H3.67461V12.95H2.97461V1.05ZM10.3246 1.05H11.0246V12.95H10.3246V1.05Z" fill="#27ADBD" />
              <path d="M2.44922 2.45H4.19922V4.2H2.44922V2.45ZM2.44922 9.8H4.19922V11.55H2.44922V9.8ZM9.79922 2.45H11.5492V4.2H9.79922V2.45ZM9.79922 9.8H11.5492V11.55H9.79922V9.8Z" fill="#27ADBD" />
            </svg>
            <span className="text-xs text-white/70 font-medium">
              画布排版 · {validImages.length}/{outputCount} 张
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {allCompleted && !confirmed && (
            <>
              {isReordering ? (
                <>
                  <button
                    onClick={() => {
                      setIsReordering(false);
                      setReorderSequence([]);
                    }}
                    className="h-8 px-3 flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-lg text-xs text-white/80 hover:text-white transition-colors border border-white/10"
                  >
                    取消
                  </button>
                  <button
                    onClick={confirmReorder}
                    disabled={reorderSequence.length === 0}
                    className="h-8 px-4 flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-lg text-xs text-white/80 hover:text-white transition-colors border border-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    应用排序 ({reorderSequence.length}/{validImages.length})
                  </button>
                  <button
                    onClick={confirmStitch}
                    disabled={validImages.length < 2 || isExporting}
                    className="h-8 px-4 flex items-center gap-1.5 bg-linear-to-r from-[#FCA164] via-[#D24DDA] to-[#652DF5] rounded-lg text-xs text-white font-medium transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_2px_12px_rgba(210,77,218,0.3)]"
                  >
                    {isExporting ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {stitchTaskId ? "后端拼接中..." : "提交中..."}
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" strokeLinecap="round" />
                          <path d="M3 9h18M3 15h18" strokeLinecap="round" />
                        </svg>
                        确认拼接
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={startReorder}
                    className="h-8 px-3 flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-lg text-xs text-white/80 hover:text-white transition-colors border border-white/10"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="3" y1="6" x2="21" y2="6" strokeLinecap="round" />
                      <line x1="3" y1="12" x2="21" y2="12" strokeLinecap="round" />
                      <line x1="3" y1="18" x2="21" y2="18" strokeLinecap="round" />
                      <circle cx="8" cy="6" r="1.5" fill="currentColor" />
                      <circle cx="16" cy="12" r="1.5" fill="currentColor" />
                      <circle cx="10" cy="18" r="1.5" fill="currentColor" />
                    </svg>
                    重新排序
                  </button>
                  <button
                    onClick={handleAutoStitchDownload}
                    disabled={validImages.length < 2 || isExporting}
                    className="h-8 px-4 flex items-center gap-1.5 bg-linear-to-r from-[#FCA164] via-[#D24DDA] to-[#652DF5] rounded-lg text-xs text-white font-medium transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_2px_12px_rgba(210,77,218,0.3)]"
                  >
                    {isExporting ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {stitchTaskId ? "后端拼接中..." : "提交中..."}
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        下载详情页
                      </>
                    )}
                  </button>
                </>
              )}
            </>
          )}
          {confirmed && stitchedPreviewUrl && (
            <>
              <button
                onClick={() => {
                  setConfirmed(false);
                  revokeStitchedUrl();
                  setStitchedPreviewUrl(null);
                  setIsReordering(false);
                  setReorderSequence([]);
                }}
                className="h-8 px-3 flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-lg text-xs text-white/70 hover:text-white transition-colors border border-white/10"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                退出重排序
              </button>
              <button
                onClick={handleResetStitch}
                className="h-8 px-3 flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-lg text-xs text-white/80 hover:text-white transition-colors border border-white/10"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                重新排版
              </button>
              <button
                onClick={() => onPreview?.(stitchedPreviewUrl)}
                className="h-8 px-3 flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-lg text-xs text-white/80 hover:text-white transition-colors border border-white/10"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                预览
              </button>
              {onFavorite && (
                <button
                  onClick={() => onFavorite(stitchedPreviewUrl)}
                  className="h-8 px-3 flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-lg text-xs text-white/80 hover:text-white transition-colors border border-white/10"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  收藏
                </button>
              )}
              {onPublish && (
                <button
                  onClick={() => onPublish(stitchedPreviewUrl)}
                  className="h-8 px-3 flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-lg text-xs text-white/80 hover:text-white transition-colors border border-white/10"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points="16 6 12 2 8 6" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="12" y1="2" x2="12" y2="15" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  发布
                </button>
              )}
              <button
                onClick={handleDownloadStitched}
                className="h-8 px-4 flex items-center gap-1.5 bg-linear-to-r from-[#FCA164] via-[#D24DDA] to-[#652DF5] rounded-lg text-xs text-white font-medium transition-all hover:brightness-110 shadow-[0_2px_12px_rgba(210,77,218,0.3)]"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                下载详情页
              </button>
            </>
          )}
        </div>
      </div>

      {/* Split content area - 50/50 layout */}
      <div className="flex-1 flex min-h-0">
        {/* Left: 3×N grid of individual images */}
        <div className="w-1/2 shrink-0 border-r border-white/10 flex flex-col">
          <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
            <span className="text-xs text-white/50 font-medium">
              {isSelectMode ? `已选择 ${selectedIndexes.size}/${validImages.length} 张` : isReordering ? "点击图片设置顺序" : "生成图片"}
            </span>
            <div className="flex items-center gap-2">
              {isReordering && reorderSequence.length > 0 && (
                <span className="text-xs text-[#FCA164]">
                  已选 {reorderSequence.length} 张
                </span>
              )}
              {/* Batch download button - show when not reordering and not confirmed */}
              {!isReordering && !confirmed && allCompleted && validImages.length > 0 && !isSelectMode && (
                <div className="relative" ref={downloadMenuRef}>
                  <button
                    onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                    className="h-7 px-2.5 flex items-center gap-1.5 bg-white/10 hover:bg-white/15 rounded-md text-[11px] text-white/70 hover:text-white transition-colors border border-white/10"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    批量下载
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  {showDownloadMenu && (
                    <div className="absolute top-full right-0 mt-1 w-32 py-1 bg-[#1C1C1C] rounded-lg border border-white/10 shadow-lg z-30">
                      <button
                        onClick={handleDownloadAll}
                        className="w-full px-3 py-2 text-left text-xs text-white/80 hover:text-white hover:bg-white/5 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        全部下载
                      </button>
                      <button
                        onClick={handleEnterSelectMode}
                        className="w-full px-3 py-2 text-left text-xs text-white/80 hover:text-white hover:bg-white/5 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 11l3 3L22 4" strokeLinecap="round" strokeLinejoin="round" />
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                        </svg>
                        选择下载
                      </button>
                    </div>
                  )}
                </div>
              )}
              {/* Select mode actions */}
              {isSelectMode && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCancelSelectMode}
                    className="h-7 px-2.5 flex items-center gap-1 bg-white/10 hover:bg-white/15 rounded-md text-[11px] text-white/70 hover:text-white transition-colors border border-white/10"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleConfirmSelectedDownload}
                    disabled={selectedIndexes.size === 0}
                    className="relative h-[31px] overflow-hidden rounded-lg border-none px-3 text-[11px] font-medium text-[#00FAE5] transition-[filter] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-[url('/Frame%20127.svg')] bg-[length:100%_100%] bg-center bg-no-repeat"
                    />
                    <span className="relative z-10 flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      下载已选({selectedIndexes.size})
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.15)_transparent] will-change-transform transform-[translateZ(0)]">
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: outputCount }).map((_, idx) => {
                const img = images[idx];
                const slotState = getLayoutSlotState({
                  image: img,
                  failed: failedIndexes.includes(idx),
                  regenerating: regeneratingIndexes.includes(idx),
                  status,
                });
                const isInSequence = reorderSequence.includes(idx);
                const sequenceNum = reorderSequence.indexOf(idx) + 1;
                const orderNum = displayOrder.indexOf(idx) + 1;
                const isSelected = selectedIndexes.has(idx);

                return (
                  <div
                    key={idx}
                    onClick={() => {
                      if (isSelectMode && img) {
                        toggleSelectIndex(idx);
                      } else if (isReordering) {
                        handleReorderClick(idx);
                      } else if (slotState === "image" && img) {
                        onPreview?.(img, idx);
                      }
                    }}
                    className={`relative rounded-lg overflow-hidden transition-all duration-200 contain-[layout_style_paint] group/card ${isSelectMode
                        ? img
                          ? isSelected
                            ? "ring-2 ring-[#00FAE5] shadow-[0_0_16px_rgba(0,250,229,0.25)] cursor-pointer"
                            : "ring-1 ring-white/15 cursor-pointer hover:ring-white/30"
                          : "ring-1 ring-white/10 opacity-40"
                        : isReordering
                          ? img
                            ? isInSequence
                              ? "ring-2 ring-[#00FAE5] shadow-[0_0_16px_rgba(0,250,229,0.25)] cursor-pointer scale-[1.01]"
                              : "ring-1 ring-white/15 cursor-pointer hover:ring-white/30 hover:scale-[1.01]"
                            : "ring-1 ring-white/10 opacity-40"
                          : confirmed
                            ? "ring-1 ring-white/10 opacity-60"
                            : "ring-1 ring-white/10 hover:ring-white/20"
                      }`}
                  >
                    {slotState === "regenerating" ? (
                      <>
                        {img
                          ? <img src={img} alt="" className="w-full block opacity-20" decoding="async" draggable={false} />
                          : <div className="w-full aspect-3/4" />}
                        <div className="absolute inset-0 flex flex-col items-center justify-center"
                          style={{ background: "linear-gradient(135deg, rgba(107,189,252,0.15) 0%, rgba(152,134,251,0.15) 50%, rgba(210,77,218,0.15) 100%)" }}>
                          <div className="w-6 h-6 border-2 border-white/30 border-t-white/80 rounded-full animate-spin mb-1.5" />
                          <span className="text-[10px] text-white/60">重新生成中...</span>
                        </div>
                      </>
                    ) : slotState === "failed" ? (
                      <>
                        <div className="w-full aspect-3/4" />
                        <div className="absolute inset-0 flex flex-col items-center justify-center"
                          style={{ background: "linear-gradient(135deg, #2D1B2E 0%, #1B1025 50%, #1A0A1E 100%)" }}>
                          <div className="w-8 h-8 mb-1.5 rounded-full bg-red-500/10 flex items-center justify-center">
                            <svg className="w-4 h-4 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
                            </svg>
                          </div>
                          <span className="text-red-400 font-medium text-[10px]">生成失败</span>
                          {onRegenerateSingle && (
                            <div className="absolute top-1 right-1 z-10">
                              <ActionIconButton
                                label="重新生成"
                                className="w-6 h-6 rounded bg-black/70 hover:bg-black/90 flex items-center justify-center text-white/60 hover:text-white transition-colors"
                                onClick={(e) => { e.stopPropagation(); onRegenerateSingle(idx); }}
                              >
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </ActionIconButton>
                            </div>
                          )}
                        </div>
                      </>
                    ) : slotState === "image" && img ? (
                      <>
                        <img
                          src={tonePreview?.idx === idx ? tonePreview.url : img}
                          alt={`图片 ${idx + 1}`}
                          className="w-full block"
                          decoding="async"
                          draggable={false}
                        />
                        {toneApplyingIndexes.has(idx) && (
                          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/25">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-label="调色应用中" />
                          </div>
                        )}

                        {/* Checkbox for select mode */}
                        {isSelectMode && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleSelectIndex(idx); }}
                            className={`absolute top-1.5 left-1.5 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all z-20 ${selectedIndexes.has(idx)
                                ? "bg-white border-white shadow-[0_1px_4px_rgba(0,0,0,0.2)]"
                                : "bg-white border-white/80 shadow-[0_1px_4px_rgba(0,0,0,0.15)]"
                              }`}
                          >
                            {selectedIndexes.has(idx) && (
                              <svg className="w-3.5 h-3.5 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </button>
                        )}

                        <div
                          className={`absolute top-1.5 left-1.5 min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-lg transition-all ${isReordering && isInSequence
                              ? "bg-linear-to-br from-[#FCA164] via-[#D24DDA] to-[#652DF5] scale-110"
                              : isSelectMode ? "hidden"
                                : "bg-black/70 backdrop-blur-xs"
                            }`}
                        >
                          {isReordering && isInSequence ? sequenceNum : orderNum}
                        </div>
                        {!isReordering && !confirmed && renderImageInlineActions(img, idx)}
                        {renderRefineOverlay(idx)}
                        {isReordering && !isInSequence && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity">
                            <div className="flex flex-col items-center gap-0.5">
                              <div className="w-6 h-6 rounded-full border-2 border-dashed border-white/50 flex items-center justify-center">
                                <svg className="w-3 h-3 text-white/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                                </svg>
                              </div>
                              <span className="text-[9px] text-white/70 font-medium">添加</span>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="w-full aspect-3/4" />
                        <div className="absolute inset-0 overflow-hidden">
                          <GeneratingPlaceholder
                            progress={taskProgresses[idx] || 0}
                            statusHint={statusHint}
                          />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: Detail page preview with zoom */}
        <div
          ref={rightPanelRef}
          className="w-1/2 flex flex-col overflow-hidden"
        >
          {/* Zoom control bar */}
          <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-3.5 h-3.5 text-white/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="4" y="2" width="16" height="20" rx="2" />
                <path d="M4 7h16M4 12h16M4 17h16" strokeDasharray="2 2" />
              </svg>
              <span className="text-xs text-white/40 font-medium">
                {confirmed ? "拼接完成 · 详情页预览" : isReordering ? `排序预览 · 已选 ${reorderSequence.length} 张` : `详情页预览 · ${layoutDetailOrder.length} 张`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setZoomLevel((prev) => Math.max(10, prev - 10))}
                className="w-6 h-6 flex items-center justify-center rounded bg-white/8 hover:bg-white/15 text-white/60 hover:text-white transition-colors"
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
                </svg>
              </button>
              <span className="text-[11px] text-white/50 font-mono min-w-[36px] text-center">{zoomLevel}%</span>
              <button
                onClick={() => setZoomLevel((prev) => Math.min(200, prev + 10))}
                className="w-6 h-6 flex items-center justify-center rounded bg-white/8 hover:bg-white/15 text-white/60 hover:text-white transition-colors"
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" strokeLinecap="round" />
                  <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>

          {/* Zoomable preview area */}
          <div
            ref={scrollContainerCallbackRef}
            className={`flex-1 overflow-y-auto overflow-x-hidden flex items-start justify-center p-4 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.15)_transparent] will-change-transform transform-[translateZ(0)] ${isDragging ? "cursor-grabbing select-none" : "cursor-grab"}`}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
          >
            <div
              className="transition-transform duration-150 origin-top"
              style={{ width: `${zoomLevel}%` }}
            >
              {confirmed && stitchedPreviewUrl ? (
                <div className="w-full">
                  <div className="rounded-xl overflow-hidden border border-white/10 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
                    <img
                      src={stitchedPreviewUrl}
                      alt="拼接详情页"
                      className="w-full block"
                      decoding="async"
                      draggable={false}
                    />
                  </div>
                  <div className="mt-2 text-center">
                    <span className="text-[10px] text-white/30">
                      {displayOrder.length} 张图片已拼接
                    </span>
                  </div>
                </div>
              ) : isReordering && layoutDetailOrder.length === 0 ? (
                <div className="w-full flex flex-col items-center justify-center py-20">
                  <svg className="w-12 h-12 text-white/15 mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <rect x="4" y="2" width="16" height="20" rx="2" />
                    <path d="M4 7h16M4 12h16M4 17h16" strokeDasharray="2 2" />
                  </svg>
                  <p className="text-sm text-white/30">点击左侧图片添加到详情页</p>
                  <p className="text-xs text-white/20 mt-1">按点击顺序拼接，支持选择部分图片</p>
                </div>
              ) : (
                <div className="w-full">
                  <div className="rounded-xl overflow-hidden border border-white/10 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
                    {layoutDetailOrder.map((imgIdx, pos) => {
                      const img = images[imgIdx];
                      const isRegenerating = regeneratingIndexes.includes(imgIdx);
                      return isRegenerating ? (
                        <div key={`regen-${imgIdx}-${pos}`} className="relative w-full aspect-3/4">
                          {img && <img src={img} alt="" className="w-full block opacity-20" decoding="async" draggable={false} />}
                          {!img && <div className="w-full aspect-3/4" />}
                          <div className="absolute inset-0 flex flex-col items-center justify-center"
                            style={{ background: "linear-gradient(135deg, rgba(107,189,252,0.15) 0%, rgba(152,134,251,0.15) 50%, rgba(210,77,218,0.15) 100%)" }}>
                            <div className="w-8 h-8 border-2 border-white/30 border-t-white/80 rounded-full animate-spin mb-2" />
                            <span className="text-xs text-white/60">重新生成中...</span>
                          </div>
                          {pos < layoutDetailOrder.length - 1 && (
                            <div className="absolute bottom-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-white/10 to-transparent" />
                          )}
                        </div>
                      ) : img ? (
                        <div
                          key={`${imgIdx}-${pos}`}
                          className="relative group/stitch-item"
                        >
                          <img
                            src={tonePreview?.idx === imgIdx ? tonePreview.url : img}
                            alt={`排版 ${pos + 1}`}
                            className="w-full block"
                            decoding="async"
                            loading="lazy"
                            draggable={false}
                          />
                          {toneApplyingIndexes.has(imgIdx) && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/25">
                              <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-label="调色应用中" />
                            </div>
                          )}
                          <div className="absolute top-1 left-1 min-w-[16px] h-4 px-0.5 rounded bg-black/60 backdrop-blur-xs flex items-center justify-center text-[8px] text-white/70 font-medium opacity-0 group-hover/stitch-item:opacity-100 transition-opacity">
                            {pos + 1}
                          </div>
                          {renderImageInlineActions(img, imgIdx)}
                          {renderRefineOverlay(imgIdx)}
                          {pos < layoutDetailOrder.length - 1 && (
                            <div className="absolute bottom-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-white/10 to-transparent" />
                          )}
                        </div>
                      ) : (
                        <div
                          key={`placeholder-${imgIdx}-${pos}`}
                          className="w-full aspect-3/4 overflow-hidden"
                        >
                          <GeneratingPlaceholder
                            progress={taskProgresses[imgIdx] || 0}
                            statusHint={statusHint}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
